import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { HeaderPaddingSafeAreaComponent } from '../../header-padding-safe-area/header-padding-safe-area.component';

/**
 * En-tête des sous-pages : « ‹ Retour », actions à droite, puis le titre et son
 * sous-titre. Un seul dessin pour toutes les pages de second niveau (météo,
 * fiche plante, planche, historiques…), bâti sur l'en-tête de la planche :
 * vrai bouton de 44 px, jetons Terreau, titre en police d'affichage.
 *
 * L'hôte est en `display: contents` : la barre et le bloc de titre sont des
 * enfants directs de la page, ce qui permet à la barre de rester collée en
 * haut (`sticky`) sur toute la hauteur de la page.
 *
 * Usage :
 * ```html
 * <vw-page-header [title]="'alerts.title' | translate" (back)="onBackClicked()">
 *   <button actions …>…</button>
 * </vw-page-header>
 * ```
 */
@Component({
  selector: 'vw-page-header',
  standalone: true,
  imports: [TranslatePipe, HeaderPaddingSafeAreaComponent],
  templateUrl: './page-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  // Un `title="…"` écrit en dur resterait aussi en attribut HTML sur l'hôte (infobulle au survol) :
  // on le retire, l'entrée garde sa valeur.
  host: { class: 'contents', '[attr.title]': 'null' },
})
export class PageHeaderComponent {
  /** Titre déjà traduit ; vide : seule la barre « Retour » s'affiche. */
  @Input() title?: string | null;
  /** Ligne d'appoint sous le titre (lieu, date, nombre…), déjà traduite. */
  @Input() subtitle?: string | null;
  /** Libellé du retour déjà traduit (« Retour à la répartition ») ; sinon « Retour ». */
  @Input() backLabel?: string | null;
  /** Fond de la page : `light` (vert pâle, `bg-vw-bg`) ou `white`. Règle la bande de zone sûre et la barre collée. */
  @Input() look: 'light' | 'white' = 'light';
  /** Page plein écran : réserve la zone sûre du téléphone (encoche). Faux quand le parent la gère déjà (panneau, feuille). */
  @Input() safeArea = true;
  /** La barre « Retour » reste en haut quand la page défile. */
  @Input() sticky = false;
  /** Posé dans un conteneur qui a déjà ses marges latérales (formulaire intégré, feuille). */
  @Input() inset = false;
  @Output() back = new EventEmitter<void>();

  onBack(): void {
    this.back.emit();
  }
}
